/**
 * POS Products Admin
 *
 * Admin interface for managing store products:
 * - List all products with search/filter
 * - Add/edit/delete products
 * - Manage categories
 * - Configure quick buttons
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import CatalogMatchPanel from '../../components/Store/CatalogMatchPanel';
import { getCategoryColor } from '../../constants/posColors';

interface ProductCategory {
  id: number;
  name: string;
  slug: string;
  color: string;
  display_order: number;
  is_active: boolean;
  is_manufactured_feed: boolean;
  product_count: number;
}

interface StoreProduct {
  id: number;
  name: string;
  sku: string;
  upc: string;
  barcode: string;
  category: string;
  category_display: string;
  product_category_id: number | null;
  product_category_name: string | null;
  retail_price: number;
  // Treasure Valley retail (MSRP). null when the product isn't sold in TV.
  tv_retail_price: number | null;
  wholesale_price: number;
  effective_price: number;
  has_formula: boolean;
  // The formula this bag is linked to. A bag delivers for its LINKED formula, whatever
  // its name says (Brad, 2026-09-25). Null = not linked.
  feed_formula_id: number | null;
  feed_formula_name?: string | null;
  base_price: number | null;
  pos_price_override: number | null;
  size_lbs: number;
  is_active: boolean;
  is_byoc: boolean;
  is_variable_price: boolean;
  quick_button: boolean;
  available_bridge_club: boolean;
  // BC Bulk visibility lives on the linked FeedFormula. null when the
  // product isn't formula-linked (checkbox disabled).
  available_bridge_club_bulk: boolean | null;
  display_order: number;
  tax_rate: number;
  is_taxable: boolean;
  tax_exempt_eligible: boolean;
  margin: number;
  supplier: string;
}

const CATEGORY_CHOICES = [
  { value: 'TINY_DINOSAUR', label: 'Tiny Dinosaur Feed' },
  { value: 'FIVE_STAR_POULTRY', label: 'Five Star Poultry' },
  { value: 'FIVE_STAR_RABBIT', label: 'Five Star Rabbit' },
  { value: 'FIVE_STAR_SWINE', label: 'Five Star Swine' },
  { value: 'FIVE_STAR_SHEEP_GOAT', label: 'Five Star Sheep & Goat' },
  { value: 'FIVE_STAR_ALL_STOCK', label: 'Five Star All Stock' },
  { value: 'FIVE_STAR_EQUINE', label: 'Five Star Equine' },
  { value: 'OTHER', label: 'Other' },
];

const FORM_CHOICES = [
  { value: 'PELLET', label: 'Pellet' },
  { value: 'CRUMBLE', label: 'Crumble' },
  { value: 'MASH', label: 'Mash' },
  { value: 'WHOLE', label: 'Whole Grain' },
  { value: 'MIXED', label: 'Mixed' },
];

const emptyProduct = {
  name: '',
  sku: '',
  upc: '',
  barcode: '',
  category: 'OTHER',
  form: 'PELLET',
  product_category_id: null as number | null,
  retail_price: 0,
  tv_retail_price: null as number | null,
  wholesale_price: 0,
  effective_price: 0,
  has_formula: false,
  feed_formula_id: null as number | null,
  base_price: null as number | null,
  pos_price_override: null as number | null,
  size_lbs: 40,
  supplier: '',
  supplier_sku: '',
  is_active: true,
  is_byoc: false,
  is_variable_price: false,
  quick_button: false,
  available_bridge_club: false,
  available_bridge_club_bulk: null as boolean | null,
  display_order: 0,
  tax_rate: 0,
  is_taxable: true,
  tax_exempt_eligible: false,
  notes: '',
};

const POSProductsAdmin: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [products, setProducts] = useState<StoreProduct[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<typeof emptyProduct | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);

  // View mode: 'products' or 'categories'
  const [viewMode, setViewMode] = useState<'products' | 'categories'>('products');

  // Category modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Partial<ProductCategory> | null>(null);

  const loadProducts = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (showInactive) params.append('show_inactive', 'true');
      const response = await api.get(`/store/products/?${params.toString()}`);
      setProducts(response.data || []);
    } catch (err: any) {
      console.error('Failed to load products:', err);
      setError('Failed to load products');
    }
  }, [showInactive]);

  const loadCategories = useCallback(async () => {
    try {
      const response = await api.get('/store/categories/');
      setCategories(response.data || []);
    } catch (err: any) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    setLoading(true);
    Promise.all([loadProducts(), loadCategories()]).finally(() => setLoading(false));
  }, [user, navigate, loadProducts, loadCategories]);

  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3000);
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch =
      !searchTerm ||
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (p.barcode && p.barcode.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesCategory =
      !filterCategory ||
      p.category === filterCategory ||
      (p.product_category_id && p.product_category_id.toString() === filterCategory);

    return matchesSearch && matchesCategory;
  });

  const handleAddProduct = () => {
    setEditingProduct({ ...emptyProduct });
    setEditingId(null);
    setShowModal(true);
  };

  // Formula options for the edit form, loaded once.
  const [formulaOptions, setFormulaOptions] = useState<{ id: number; name: string; code: string; feed_grade: string | null }[]>([]);
  useEffect(() => {
    api.get('/store/products/formula-options/')
      .then((r) => setFormulaOptions(r.data.formulas || []))
      .catch(() => setFormulaOptions([]));
  }, []);
  const GRADE_LABEL: Record<string, string> = { FIVE_STAR: 'Five Star', ESSENTIAL: 'Essentials', PLUS: 'Plus' };

  const handleEditProduct = async (productId: number) => {
    try {
      const response = await api.get(`/store/products/${productId}/`);
      setEditingProduct(response.data);
      setEditingId(productId);
      setShowModal(true);
    } catch (err) {
      showToast('error', 'Failed to load product details');
    }
  };

  const handleSaveProduct = async () => {
    if (!editingProduct) return;

    if (!editingProduct.name || !editingProduct.sku) {
      showToast('error', 'Name and SKU are required');
      return;
    }

    setSaving(true);
    try {
      if (editingId) {
        await api.put(`/store/products/${editingId}/`, editingProduct);
        showToast('success', 'Product updated successfully');
      } else {
        await api.post('/store/products/create/', editingProduct);
        showToast('success', 'Product created successfully');
      }
      setShowModal(false);
      setEditingProduct(null);
      setEditingId(null);
      loadProducts();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProduct = async (productId: number) => {
    if (!window.confirm('Are you sure you want to deactivate this product?')) return;

    try {
      await api.delete(`/store/products/${productId}/`);
      showToast('success', 'Product deactivated');
      loadProducts();
    } catch (err) {
      showToast('error', 'Failed to deactivate product');
    }
  };

  const handleAddCategory = () => {
    setEditingCategory({ name: '', color: '#3F2021', display_order: 0, is_active: true });
    setShowCategoryModal(true);
  };

  const handleEditCategory = (cat: ProductCategory) => {
    setEditingCategory(cat);
    setShowCategoryModal(true);
  };

  const handleSaveCategory = async () => {
    if (!editingCategory?.name) {
      showToast('error', 'Category name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingCategory.id) {
        await api.put(`/store/categories/${editingCategory.id}/`, editingCategory);
        showToast('success', 'Category updated');
      } else {
        await api.post('/store/categories/', editingCategory);
        showToast('success', 'Category created');
      }
      setShowCategoryModal(false);
      setEditingCategory(null);
      loadCategories();
    } catch (err: any) {
      showToast('error', err.response?.data?.error || 'Failed to save category');
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;

  return (
    <div style={styles.page}>
      <main style={styles.main}>
        {/* Toast */}
        {toast && (
          <div
            style={{
              ...styles.toast,
              backgroundColor: toast.type === 'success' ? '#d4edda' : '#f8d7da',
              color: toast.type === 'success' ? '#155724' : '#721c24',
            }}
          >
            {toast.message}
          </div>
        )}

        {/* Header */}
        <div style={styles.header}>
          <div>
            <h1 style={styles.title}>POS Product Management</h1>
            <button style={styles.backLink} onClick={() => navigate('/admin')}>
              &larr; Back to Admin
            </button>
          </div>
          <div style={styles.tabs}>
            <button
              style={viewMode === 'products' ? styles.tabActive : styles.tab}
              onClick={() => setViewMode('products')}
            >
              Products
            </button>
            <button
              style={viewMode === 'categories' ? styles.tabActive : styles.tab}
              onClick={() => setViewMode('categories')}
            >
              Categories
            </button>
            <button
              style={styles.tab}
              onClick={() => navigate('/admin/feed-formulas/bulk-visibility')}
              title="Curate which feed formulas appear in the Bridge Club bulk-order catalog"
            >
              Manage Bulk Visibility &rarr;
            </button>
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {viewMode === 'products' ? (
          <>
            {/* Product Filters */}
            <div style={styles.filters}>
              <input
                type="text"
                placeholder="Search by name, SKU, or barcode..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={styles.searchInput}
              />
              <select
                value={filterCategory}
                onChange={e => setFilterCategory(e.target.value)}
                style={styles.filterSelect}
              >
                <option value="">All Categories</option>
                <optgroup label="POS Categories">
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id.toString()}>
                      {cat.name}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Feed Categories">
                  {CATEGORY_CHOICES.map(c => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </optgroup>
              </select>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={e => setShowInactive(e.target.checked)}
                />
                Show Inactive
              </label>
              <button style={styles.addBtn} onClick={handleAddProduct}>
                + Add Product
              </button>
            </div>

            {/* Products Table */}
            {loading ? (
              <div style={styles.loading}>Loading products...</div>
            ) : (
              <div style={styles.tableContainer}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Product</th>
                      <th style={styles.th}>SKU</th>
                      <th style={styles.th}>Category</th>
                      <th style={styles.th}>Base Price</th>
                      <th style={styles.th}>TV Price</th>
                      <th style={styles.th}>POS Override</th>
                      <th style={styles.th}>Effective</th>
                      <th style={styles.th}>Flags</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredProducts.map(product => (
                      <tr
                        key={product.id}
                        style={{
                          ...styles.tr,
                          opacity: product.is_active ? 1 : 0.5,
                        }}
                      >
                        <td style={styles.td}>
                          <div style={styles.productName}>{product.name}</div>
                          <div style={styles.productSize}>{product.size_lbs} lb</div>
                        </td>
                        <td style={styles.td}>{product.sku}</td>
                        <td style={styles.td}>
                          {product.product_category_name || product.category_display}
                        </td>
                        <td style={styles.td}>
                          {product.base_price != null ? (
                            <span title="From formula">${product.base_price.toFixed(2)} <span style={{ fontSize: '10px', color: '#888' }}>F</span></span>
                          ) : (
                            <span>${product.retail_price.toFixed(2)}</span>
                          )}
                        </td>
                        <td style={styles.td}>
                          {product.tv_retail_price != null ? (
                            <span style={{ color: '#166534', fontWeight: 'bold' }} title="Treasure Valley retail (MSRP)">${product.tv_retail_price.toFixed(2)}</span>
                          ) : (
                            <span style={{ color: '#aaa' }} title="No Treasure Valley price set">—</span>
                          )}
                        </td>
                        <td style={styles.td}>
                          {product.pos_price_override != null ? (
                            <span style={{ color: '#2563eb', fontWeight: 'bold' }}>${product.pos_price_override.toFixed(2)}</span>
                          ) : (
                            <span style={{ color: '#aaa' }}>--</span>
                          )}
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontWeight: 'bold' }}>${product.effective_price.toFixed(2)}</span>
                        </td>
                        <td style={styles.td}>
                          <div style={styles.flags}>
                            {product.has_formula && <span style={{ ...styles.flag, background: '#dbeafe', color: '#1e40af' }}>Formula</span>}
                            {product.is_byoc && <span style={styles.flag}>BYOC</span>}
                            {product.is_variable_price && <span style={styles.flag}>VAR$</span>}
                            {product.quick_button && <span style={styles.flagQuick}>QB</span>}
                            {product.available_bridge_club && <span style={styles.flagBridgeClub}>BC</span>}
                            {product.has_formula && (
                              product.available_bridge_club_bulk
                                ? <span style={{ ...styles.flag, background: '#dcfce7', color: '#166534' }} title="In Bridge Club bulk-order catalog">BULK ✓</span>
                                : <span style={{ ...styles.flag, background: '#f3f4f6', color: '#9ca3af' }} title="Hidden from Bridge Club bulk-order catalog">BULK –</span>
                            )}
                            {product.tax_exempt_eligible && <span style={styles.flagTaxExempt}>TE</span>}
                            {!product.is_active && <span style={styles.flagInactive}>OFF</span>}
                          </div>
                        </td>
                        <td style={styles.td}>
                          <button
                            style={styles.editBtn}
                            onClick={() => handleEditProduct(product.id)}
                          >
                            Edit
                          </button>
                          {product.is_active && (
                            <button
                              style={styles.deleteBtn}
                              onClick={() => handleDeleteProduct(product.id)}
                            >
                              Deactivate
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {filteredProducts.length === 0 && (
                      <tr>
                        <td colSpan={9} style={styles.emptyRow}>
                          No products found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </>
        ) : (
          /* Categories View */
          <div style={styles.categoriesSection}>
            <div style={styles.categoryHeader}>
              <h2 style={styles.sectionTitle}>POS Categories</h2>
              <button style={styles.addBtn} onClick={handleAddCategory}>
                + Add Category
              </button>
            </div>
            <div style={styles.categoryGrid}>
              {categories.map(cat => {
                // Get the actual POS color from constants
                const posColor = getCategoryColor(cat.name);
                return (
                  <div
                    key={cat.id}
                    style={{
                      ...styles.categoryCard,
                      borderLeftColor: posColor.bg,
                      opacity: cat.is_active ? 1 : 0.5,
                    }}
                  >
                    <div style={styles.categoryInfo}>
                      <span style={styles.categoryName}>{cat.name}</span>
                      <span style={styles.categoryCount}>{cat.product_count} products</span>
                    </div>
                    <div
                      style={{ ...styles.colorSwatch, backgroundColor: posColor.bg }}
                      title={`POS color: ${posColor.bg}`}
                    />
                    <button style={styles.editBtn} onClick={() => handleEditCategory(cat)}>
                      Edit
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </main>

      {/* Product Edit Modal */}
      {showModal && editingProduct && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingId ? 'Edit Product' : 'Add New Product'}
              </h2>
              <button style={styles.closeBtn} onClick={() => setShowModal(false)}>
                &times;
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGrid}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Name *</label>
                  <input
                    type="text"
                    value={editingProduct.name}
                    onChange={e =>
                      setEditingProduct({ ...editingProduct, name: e.target.value })
                    }
                    style={styles.input}
                    placeholder="Product name"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>SKU *</label>
                  <input
                    type="text"
                    value={editingProduct.sku}
                    onChange={e =>
                      setEditingProduct({ ...editingProduct, sku: e.target.value })
                    }
                    style={styles.input}
                    placeholder="SKU"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Barcode/UPC</label>
                  <input
                    type="text"
                    value={editingProduct.barcode || editingProduct.upc || ''}
                    onChange={e =>
                      setEditingProduct({
                        ...editingProduct,
                        barcode: e.target.value,
                        upc: e.target.value,
                      })
                    }
                    style={styles.input}
                    placeholder="Barcode"
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>POS Category</label>
                  <select
                    value={editingProduct.product_category_id || ''}
                    onChange={e =>
                      setEditingProduct({
                        ...editingProduct,
                        product_category_id: e.target.value ? parseInt(e.target.value) : null,
                      })
                    }
                    style={styles.input}
                  >
                    <option value="">-- Select Category --</option>
                    {categories.map(cat => (
                      <option key={cat.id} value={cat.id}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Feed Category</label>
                  <select
                    value={editingProduct.category}
                    onChange={e =>
                      setEditingProduct({ ...editingProduct, category: e.target.value })
                    }
                    style={styles.input}
                  >
                    {CATEGORY_CHOICES.map(c => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                {/* Match to catalog: fills tv_retail_price and UPC from the
                    distributor catalog, and can clear Form at the same time.
                    Only on an EXISTING row - a product being created has no id
                    to match against yet. */}
                {editingId && (
                  <div style={{ gridColumn: '1 / -1' }}>
                    <CatalogMatchPanel
                      storeProductId={editingId}
                      onApplied={() => {
                        // Re-read the row so the modal's own fields show what
                        // the match just wrote, rather than going stale behind
                        // the panel that changed them.
                        handleEditProduct(editingId);
                        loadProducts();
                      }}
                    />
                  </div>
                )}

                <div style={styles.formGroup}>
                  <label style={styles.label}>Form</label>
                  <select
                    value={editingProduct.form}
                    onChange={e =>
                      setEditingProduct({ ...editingProduct, form: e.target.value })
                    }
                    style={styles.input}
                  >
                    {FORM_CHOICES.map(c => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Formula</label>
                  <select
                    value={editingProduct.feed_formula_id ?? ''}
                    onChange={e =>
                      setEditingProduct({ ...editingProduct, feed_formula_id: e.target.value ? Number(e.target.value) : null, has_formula: !!e.target.value })
                    }
                    style={styles.input}
                    title="A bag delivers for the formula it is linked to, whatever its name says."
                  >
                    <option value="">Not linked to a formula</option>
                    {Array.from(new Set(formulaOptions.map(f => f.feed_grade || ''))).map(grade => (
                      <optgroup key={grade || 'ungraded'} label={GRADE_LABEL[grade] || grade || 'Ungraded'}>
                        {formulaOptions.filter(f => (f.feed_grade || '') === grade).map(f => (
                          <option key={f.id} value={f.id}>{f.name}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Retail Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingProduct.retail_price}
                    onFocus={e => e.target.select()}
                    onChange={e =>
                      setEditingProduct({
                        ...editingProduct,
                        retail_price: parseFloat(e.target.value) || 0,
                      })
                    }
                    style={styles.input}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    What Kettle Falls charges (community wholesale).
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>TV Retail Price</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingProduct.tv_retail_price ?? ''}
                    onFocus={e => e.target.select()}
                    onChange={e => {
                      const val = e.target.value;
                      setEditingProduct({
                        ...editingProduct,
                        tv_retail_price: val === '' ? null : parseFloat(val) || 0,
                      });
                    }}
                    placeholder="Treasure Valley price"
                    style={styles.input}
                  />
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    What Treasure Valley charges (MSRP). Leave blank if not sold in TV.
                  </div>
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>POS Price Override</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingProduct.pos_price_override ?? ''}
                    onFocus={e => e.target.select()}
                    onChange={e => {
                      const val = e.target.value;
                      setEditingProduct({
                        ...editingProduct,
                        pos_price_override: val === '' ? null : parseFloat(val) || 0,
                      });
                    }}
                    placeholder={editingProduct.base_price != null ? `Formula: $${editingProduct.base_price.toFixed(2)}` : 'No override'}
                    style={styles.input}
                  />
                  {editingProduct.base_price != null && (
                    <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                      Formula base price: ${editingProduct.base_price.toFixed(2)}
                    </div>
                  )}
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Wholesale Cost</label>
                  <input
                    type="number"
                    step="0.01"
                    value={editingProduct.wholesale_price}
                    onFocus={e => e.target.select()}
                    onChange={e =>
                      setEditingProduct({
                        ...editingProduct,
                        wholesale_price: parseFloat(e.target.value) || 0,
                      })
                    }
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Size (lbs)</label>
                  <input
                    type="number"
                    step="0.1"
                    value={editingProduct.size_lbs}
                    onFocus={e => e.target.select()}
                    onChange={e =>
                      setEditingProduct({
                        ...editingProduct,
                        size_lbs: parseFloat(e.target.value) || 0,
                      })
                    }
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Supplier</label>
                  <input
                    type="text"
                    value={editingProduct.supplier}
                    onChange={e =>
                      setEditingProduct({ ...editingProduct, supplier: e.target.value })
                    }
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Tax Rate (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={(editingProduct.tax_rate * 100).toFixed(2)}
                    onFocus={e => e.target.select()}
                    onChange={e =>
                      setEditingProduct({
                        ...editingProduct,
                        tax_rate: (parseFloat(e.target.value) || 0) / 100,
                      })
                    }
                    style={styles.input}
                  />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Display Order</label>
                  <input
                    type="number"
                    value={editingProduct.display_order}
                    onFocus={e => e.target.select()}
                    onChange={e =>
                      setEditingProduct({
                        ...editingProduct,
                        display_order: parseInt(e.target.value) || 0,
                      })
                    }
                    style={styles.input}
                  />
                </div>
              </div>
              <div style={styles.checkboxGrid}>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={editingProduct.is_active}
                    onChange={e =>
                      setEditingProduct({ ...editingProduct, is_active: e.target.checked })
                    }
                  />
                  Active
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={editingProduct.is_taxable}
                    onChange={e =>
                      setEditingProduct({ ...editingProduct, is_taxable: e.target.checked })
                    }
                  />
                  Taxable
                </label>
                <label style={styles.checkboxLabel} title="When checked, this product's tax is removed when Tax Exempt is active at checkout">
                  <input
                    type="checkbox"
                    checked={editingProduct.tax_exempt_eligible}
                    onChange={e =>
                      setEditingProduct({ ...editingProduct, tax_exempt_eligible: e.target.checked })
                    }
                  />
                  Tax Exempt Eligible
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={editingProduct.is_byoc}
                    onChange={e =>
                      setEditingProduct({ ...editingProduct, is_byoc: e.target.checked })
                    }
                  />
                  BYOC (Weight-based)
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={editingProduct.is_variable_price}
                    onChange={e =>
                      setEditingProduct({
                        ...editingProduct,
                        is_variable_price: e.target.checked,
                      })
                    }
                  />
                  Variable Price
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={editingProduct.quick_button}
                    onChange={e =>
                      setEditingProduct({ ...editingProduct, quick_button: e.target.checked })
                    }
                  />
                  Quick Button
                </label>
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={editingProduct.available_bridge_club}
                    onChange={e =>
                      setEditingProduct({
                        ...editingProduct,
                        available_bridge_club: e.target.checked,
                      })
                    }
                  />
                  Bridge Club Shop
                </label>
                <label
                  style={{
                    ...styles.checkboxLabel,
                    opacity: editingProduct.has_formula ? 1 : 0.5,
                  }}
                  title={
                    editingProduct.has_formula
                      ? 'Gates this feed formula’s visibility in the Bridge Club bulk-order catalog'
                      : 'Not linked to a feed formula'
                  }
                >
                  <input
                    type="checkbox"
                    disabled={!editingProduct.has_formula}
                    checked={!!editingProduct.available_bridge_club_bulk}
                    onChange={e =>
                      setEditingProduct({
                        ...editingProduct,
                        available_bridge_club_bulk: e.target.checked,
                      })
                    }
                  />
                  Bridge Club Bulk
                  <span style={{ fontSize: '11px', color: '#888', marginLeft: '6px' }}>
                    {editingProduct.has_formula
                      ? '(gates bulk-order visibility)'
                      : '(not linked to a feed formula)'}
                  </span>
                </label>
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setShowModal(false)}>
                Cancel
              </button>
              <button style={styles.saveBtn} onClick={handleSaveProduct} disabled={saving}>
                {saving ? 'Saving...' : 'Save Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Edit Modal */}
      {showCategoryModal && editingCategory && (
        <div style={styles.modalOverlay} onClick={() => setShowCategoryModal(false)}>
          <div style={styles.modalSmall} onClick={e => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>
                {editingCategory.id ? 'Edit Category' : 'Add Category'}
              </h2>
              <button style={styles.closeBtn} onClick={() => setShowCategoryModal(false)}>
                &times;
              </button>
            </div>
            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Name *</label>
                <input
                  type="text"
                  value={editingCategory.name || ''}
                  onChange={e =>
                    setEditingCategory({ ...editingCategory, name: e.target.value })
                  }
                  style={styles.input}
                />
              </div>
              {/* Color is automatic - derived from category name via posColors.ts */}
              {editingCategory.name && (
                <div style={styles.formGroup}>
                  <label style={styles.label}>POS Color (automatic)</label>
                  <div style={styles.posColorDisplay}>
                    <div
                      style={{
                        ...styles.colorSwatchLarge,
                        backgroundColor: getCategoryColor(editingCategory.name).bg,
                      }}
                    />
                    <span style={styles.posColorHex}>
                      {getCategoryColor(editingCategory.name).bg}
                    </span>
                    <span style={styles.posColorNote}>
                      Color is set by category name in posColors.ts
                    </span>
                  </div>
                </div>
              )}
              <div style={styles.formGroup}>
                <label style={styles.label}>Display Order</label>
                <input
                  type="number"
                  value={editingCategory.display_order || 0}
                  onFocus={e => e.target.select()}
                  onChange={e =>
                    setEditingCategory({
                      ...editingCategory,
                      display_order: parseInt(e.target.value) || 0,
                    })
                  }
                  style={styles.input}
                />
              </div>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={editingCategory.is_active !== false}
                  onChange={e =>
                    setEditingCategory({ ...editingCategory, is_active: e.target.checked })
                  }
                />
                Active
              </label>
              <label style={styles.checkboxLabel}>
                <input
                  type="checkbox"
                  checked={editingCategory.is_manufactured_feed === true}
                  onChange={e =>
                    setEditingCategory({ ...editingCategory, is_manufactured_feed: e.target.checked })
                  }
                />
                Manufactured feed
              </label>
              <div style={{ fontSize: '12px', color: '#666', marginTop: '-4px', marginBottom: '4px' }}>
                Feed Red Bridge manufactures — counts toward WSDA feed license tonnage.
              </div>
            </div>
            <div style={styles.modalFooter}>
              <button style={styles.cancelBtn} onClick={() => setShowCategoryModal(false)}>
                Cancel
              </button>
              <button style={styles.saveBtn} onClick={handleSaveCategory} disabled={saving}>
                {saving ? 'Saving...' : 'Save Category'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
  },
  main: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '1.5rem',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    gap: '1rem',
  },
  title: {
    fontSize: '1.75rem',
    fontWeight: 'bold',
    color: '#1a4d1a',
    margin: 0,
  },
  backLink: {
    background: 'none',
    border: 'none',
    color: '#666',
    cursor: 'pointer',
    padding: 0,
    fontSize: '0.9rem',
    marginTop: '0.5rem',
  },
  tabs: {
    display: 'flex',
    gap: '0.5rem',
  },
  tab: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#666',
  },
  tabActive: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#1a4d1a',
    border: '1px solid #1a4d1a',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '500',
    color: '#fff',
  },
  error: {
    backgroundColor: '#f8d7da',
    color: '#721c24',
    padding: '1rem',
    borderRadius: '8px',
    marginBottom: '1rem',
  },
  toast: {
    position: 'fixed',
    top: '80px',
    right: '20px',
    padding: '1rem 1.5rem',
    borderRadius: '8px',
    fontWeight: '500',
    zIndex: 1000,
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  },
  filters: {
    display: 'flex',
    gap: '1rem',
    marginBottom: '1.5rem',
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  searchInput: {
    flex: '1 1 300px',
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: '8px',
  },
  filterSelect: {
    padding: '0.75rem 1rem',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: '8px',
    minWidth: '200px',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.95rem',
    cursor: 'pointer',
  },
  addBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
  },
  loading: {
    textAlign: 'center',
    padding: '3rem',
    color: '#666',
  },
  tableContainer: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    overflow: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '1rem',
    borderBottom: '2px solid #eee',
    fontWeight: '600',
    color: '#333',
    fontSize: '0.9rem',
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: '1px solid #eee',
  },
  td: {
    padding: '0.75rem 1rem',
    verticalAlign: 'middle',
  },
  productName: {
    fontWeight: '600',
    color: '#333',
  },
  productSize: {
    fontSize: '0.85rem',
    color: '#666',
  },
  flags: {
    display: 'flex',
    gap: '0.25rem',
    flexWrap: 'wrap',
  },
  flag: {
    padding: '0.2rem 0.5rem',
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: '600',
  },
  flagQuick: {
    padding: '0.2rem 0.5rem',
    backgroundColor: '#fff3e0',
    color: '#f57c00',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: '600',
  },
  flagBridgeClub: {
    padding: '0.2rem 0.5rem',
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: '600',
  },
  flagTaxExempt: {
    padding: '0.2rem 0.5rem',
    backgroundColor: '#f3e5f5',
    color: '#7b1fa2',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: '600',
  },
  flagInactive: {
    padding: '0.2rem 0.5rem',
    backgroundColor: '#ffebee',
    color: '#c62828',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: '600',
  },
  editBtn: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    marginRight: '0.5rem',
  },
  deleteBtn: {
    padding: '0.4rem 0.75rem',
    backgroundColor: '#ffebee',
    border: '1px solid #ffcdd2',
    color: '#c62828',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  emptyRow: {
    textAlign: 'center',
    padding: '2rem',
    color: '#999',
  },
  // Categories
  categoriesSection: {
    backgroundColor: '#fff',
    borderRadius: '12px',
    padding: '1.5rem',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  categoryHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.5rem',
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#333',
    margin: 0,
  },
  categoryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
    gap: '1rem',
  },
  categoryCard: {
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    padding: '1rem 1.25rem',
    backgroundColor: '#f9f9f9',
    borderRadius: '8px',
    borderLeft: '4px solid',
  },
  categoryInfo: {
    flex: 1,
  },
  categoryName: {
    display: 'block',
    fontWeight: '600',
    color: '#333',
  },
  categoryCount: {
    display: 'block',
    fontSize: '0.85rem',
    color: '#666',
  },
  colorSwatch: {
    width: '24px',
    height: '24px',
    borderRadius: '4px',
    border: '1px solid #ddd',
  },
  colorSwatchLarge: {
    width: '40px',
    height: '40px',
    borderRadius: '6px',
    border: '2px solid #ddd',
  },
  posColorDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.75rem',
    padding: '0.75rem',
    backgroundColor: '#f9fafb',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },
  posColorHex: {
    fontSize: '0.9rem',
    color: '#333',
    fontFamily: 'monospace',
    fontWeight: '600',
  },
  posColorNote: {
    fontSize: '0.75rem',
    color: '#6b7280',
    fontStyle: 'italic',
  },
  // Modals
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
    overflowY: 'auto',
  },
  modal: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '700px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
  },
  modalSmall: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '400px',
    maxHeight: '90vh',
    overflow: 'auto',
    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '1.25rem 1.5rem',
    borderBottom: '1px solid #eee',
  },
  modalTitle: {
    fontSize: '1.25rem',
    fontWeight: '600',
    color: '#333',
    margin: 0,
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    fontSize: '1.5rem',
    color: '#999',
    cursor: 'pointer',
    padding: '0.25rem',
    lineHeight: 1,
  },
  modalBody: {
    padding: '1.5rem',
  },
  modalFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '1rem',
    padding: '1.25rem 1.5rem',
    borderTop: '1px solid #eee',
  },
  formGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '1rem',
    marginBottom: '1.5rem',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '0.85rem',
    fontWeight: '600',
    color: '#555',
    marginBottom: '0.25rem',
  },
  input: {
    padding: '0.65rem 0.75rem',
    fontSize: '1rem',
    border: '1px solid #ddd',
    borderRadius: '6px',
  },
  checkboxGrid: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '1rem 2rem',
  },
  colorInputRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
  },
  colorPicker: {
    width: '40px',
    height: '36px',
    padding: 0,
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'pointer',
  },
  cancelBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#f5f5f5',
    border: '1px solid #ddd',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
  },
  saveBtn: {
    padding: '0.75rem 1.5rem',
    backgroundColor: '#1a4d1a',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontSize: '1rem',
    fontWeight: '600',
  },
};

export default POSProductsAdmin;
